// React
import { useEffect, useState } from "react"

// Translations
import { Trans, useTrans } from "@/i18n"
import { useLang } from "@/context/lang"

// DateTime
import { DateTime } from "@/lib/dates"
import { dateFormat } from "@/constants/constants"

// UI
import { Button } from "@/components/Button"
import { Heading } from "@/components/Typography"
import { CardBody, CardWrapper, Card } from "@/components/Card"
import { useToasts } from "@/context/toasts"
import { Disclaimer } from "@/components/Disclaimer"
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog"

// Specific UI
import { ExpertExperienceLevelBusiness } from "./_components/ExpertExperienceLevelBusiness"
import { ExpertExperienceLevelPrivate } from "./_components/ExpertExperienceLevelPrivate"

// Icons
import { CheckIcon } from "@/lib/icons"

// Context
import { useInvestmentTests } from "@/context/investmentTests"
import { useCurrentUserId, useCurrentUserIdentityType } from "@/context/user"

// Hooks
import { useForceDefaultLanguage } from "@/hooks/useForceDefaultLanguage"

// GraphQL
import {
	CoreInvestmentTestCategoryChoices,
	CoreInvestmentTestExperienceChoices,
	useInvestorTestMutationsInputMutation,
	UsersUserProfileIdentityTypeChoices,
} from "@/api/graphql"

// Tracking
import { enableGoogleTagManager } from "@/lib/analytics"

/**
 * InvestmentExperience
 * @returns
 */
export const InvestorProfile = () => {
	// Context
	const toasts = useToasts()
	const { investorProfileExperience, refetch } = useInvestmentTests()
	const { id: userId } = useCurrentUserId()

	// State
	const [confirmDialogIsOpen, setConfirmDialogIsOpen] =
		useState<boolean>(false)
	const [investorExperienceLevelChoice, setInvestorExperienceLevelChoice] =
		useState<CoreInvestmentTestExperienceChoices>(
			CoreInvestmentTestExperienceChoices.Novice,
		)

	// Translations
	const t = useTrans("investor")
	const { DefaultLanguageDisclaimerComponent } = useForceDefaultLanguage()

	// When visiting this page, disable all trackers (GTM)
	useEffect(() => {
		enableGoogleTagManager(false)

		// When unmounting, enable tracking again
		return () => {
			enableGoogleTagManager(true)
		}
	}, [])

	// Mutation
	const createNewTestSubmission = useInvestorTestMutationsInputMutation()

	// When picked investor experience level
	async function handleChoiceExpertInvestor() {
		const response = await createNewTestSubmission.mutateAsync({
			input: {
				investor: userId || "",
				category: CoreInvestmentTestCategoryChoices.Experience,
				experience: CoreInvestmentTestExperienceChoices.Expert,
			},
		})
		// do we have data? then success
		if (response?.investment_test_update?.investor_test?.id) {
			toasts.addToast({
				variant: "success",
				id: `notifications-success-${Date.now()}`,
				text: t(
					"Uw investeringsprofiel is bijgewerkt naar: ervaren belegger",
				),
			})

			// Refetch data
			void refetch()

			// Scroll to top
			window.scrollTo(0, 0)
		}
	}
	async function handleChoiceNoviceInvestor() {
		const response = await createNewTestSubmission.mutateAsync({
			input: {
				investor: userId || "",
				category: CoreInvestmentTestCategoryChoices.Experience,
				experience: CoreInvestmentTestExperienceChoices.Novice,
			},
		})
		// do we have data? then success
		if (response?.investment_test_update?.investor_test?.id) {
			toasts.addToast({
				variant: "success",
				id: `notifications-success-${Date.now()}`,
				text: t(
					"Uw investeringsprofiel is bijgewerkt naar: niet-ervaren belegger",
				),
			})
			void refetch()
		}
	}

	return (
		<>
			<title>
				{t("common:common.web.title", {
					page: t("investor:investor.profile.title"),
				})}
			</title>

			<Disclaimer
				className="mb-3"
				title={t("investor:investor.generic.privacy.title")}
				message={
					<Trans
						ns="investor"
						i18nKey="investor:investor.generic.privacy.copy"
						components={{
							/* eslint-disable jsx-a11y/anchor-has-content */
							a: (
								<a
									className="font-medium text-secondary-300 hover:text-secondary-700"
									href="https://www.afm.nl/nl-nl/sector/registers/vergunningenregisters/beleggingsondernemingen/details?id=70DF7EAB-C7AA-E411-A3EB-005056BE011D"
									target="_blank"
									rel="noreferrer"
								/>
								/* eslint-enable jsx-a11y/anchor-has-content */
							),
						}}
					/>
				}
			/>

			<ConfirmDialog
				isOpen={confirmDialogIsOpen}
				onClose={() => setConfirmDialogIsOpen(false)}
				onConfirm={() => {
					if (
						investorExperienceLevelChoice ===
						CoreInvestmentTestExperienceChoices.Novice
					) {
						void handleChoiceNoviceInvestor()
					} else {
						void handleChoiceExpertInvestor()
					}
					setConfirmDialogIsOpen(false)
				}}
				description={
					{
						NOVICE: t(
							"investor:investor.profile.choice.novice.copy.confirm.novice",
						),
						EXPERT: t(
							"investor:investor.profile.choice.novice.copy.confirm.expert",
						),
					}[investorExperienceLevelChoice]
				}
			/>

			<CardWrapper className="relative">
				{Boolean(investorProfileExperience) === true && (
					<StepCompletedOverlay />
				)}

				<CardBody>
					<Heading as="h2" styleAs="h5" className="mb-3 sm:truncate">
						{t("investor:investor.profile.heading")}
					</Heading>

					<DefaultLanguageDisclaimerComponent />

					<div className="flex flex-col gap-4 md:gap-8 lg:flex-row">
						<ExpertExperience
							setInvestorExperienceLevelChoice={
								setInvestorExperienceLevelChoice
							}
							setConfirmDialogIsOpen={setConfirmDialogIsOpen}
						/>

						<NoviceExperience
							setInvestorExperienceLevelChoice={
								setInvestorExperienceLevelChoice
							}
							setConfirmDialogIsOpen={setConfirmDialogIsOpen}
						/>
					</div>
				</CardBody>
			</CardWrapper>
		</>
	)
}

/**
 * ExpertExperience
 * @returns
 */
export const ExpertExperience = ({
	setInvestorExperienceLevelChoice,
	setConfirmDialogIsOpen,
}: {
	setInvestorExperienceLevelChoice: (
		choice: CoreInvestmentTestExperienceChoices,
	) => void
	setConfirmDialogIsOpen: (isOpen: boolean) => void
}) => {
	// Context
	const identity_type = useCurrentUserIdentityType()

	// State
	const [businessCriteria, setBusinessCriteria] = useState({
		item1: false,
		item2: false,
		item3: false,
	})
	const [privateCriteria, setPrivateCriteria] = useState({
		item1: false,
		item2: false,
		item3: false,
	})

	// openExpertDialog
	function openExpertDialog() {
		setInvestorExperienceLevelChoice(
			CoreInvestmentTestExperienceChoices.Expert,
		)
		setConfirmDialogIsOpen(true)
	}

	// Open dialog if any businessCriteria is true
	useEffect(() => {
		if (Object.values(businessCriteria).some(Boolean)) {
			openExpertDialog()
		}
	}, [businessCriteria, setConfirmDialogIsOpen])

	// Open dialog if at least 2 privateCriteria are true
	useEffect(() => {
		const count = Object.values(privateCriteria).filter(Boolean).length
		if (count >= 2) {
			openExpertDialog()
		}
	}, [privateCriteria, setConfirmDialogIsOpen])

	// i18n
	const t = useTrans("investor")

	return (
		<div className="bg-gray-card flex w-full flex-col items-start justify-center rounded-md border border-gray-300 p-4 lg:w-1/2">
			<div className="grow">
				<strong className="font-bold">
					{t("investor:investor.profile.choice.expert.title")}
				</strong>
				<br />
				{t("investor:investor.profile.choice.expert.subtitle")}

				{/** Show business criteria if BUSINESS or UNAVAILABLE */}
				{identity_type ===
					UsersUserProfileIdentityTypeChoices.Business ||
				identity_type ===
					UsersUserProfileIdentityTypeChoices.Unavailable ? (
					<ExpertExperienceLevelBusiness
						businessCriteria={businessCriteria}
						setBusinessCriteria={setBusinessCriteria}
					/>
				) : null}

				{/** Show both if UNAVAILABLE */}
				{identity_type ===
				UsersUserProfileIdentityTypeChoices.Unavailable ? (
					<strong className="mt-4 font-bold">
						{t("investor:investor.profile.generic.or")}
					</strong>
				) : null}

				{/** Show private criteria if PRIVATE or UNAVAILABLE */}
				{identity_type ===
					UsersUserProfileIdentityTypeChoices.Private ||
				identity_type ===
					UsersUserProfileIdentityTypeChoices.Unavailable ? (
					<ExpertExperienceLevelPrivate
						privateCriteria={privateCriteria}
						setPrivateCriteria={setPrivateCriteria}
					/>
				) : null}
			</div>

			<Button
				className="flex-end mt-4 flex"
				onClick={() => {
					openExpertDialog()
				}}
			>
				{t("investor:investor.profile.choice.expert.button")}
			</Button>
		</div>
	)
}

/**
 * NoviceExperience
 * @param param0
 * @returns
 */
function NoviceExperience({
	setInvestorExperienceLevelChoice,
	setConfirmDialogIsOpen,
}: {
	setInvestorExperienceLevelChoice: (
		choice: CoreInvestmentTestExperienceChoices,
	) => void
	setConfirmDialogIsOpen: (isOpen: boolean) => void
}) {
	// i18n
	const t = useTrans("investor")

	// openNoviceDialog
	function openNoviceDialog() {
		setInvestorExperienceLevelChoice(
			CoreInvestmentTestExperienceChoices.Novice,
		)
		setConfirmDialogIsOpen(true)
	}

	return (
		<div className="bg-gray-card flex w-full flex-col items-start justify-center rounded-md border border-gray-300 p-4 lg:w-1/2">
			<p className="grow">
				<strong className="font-bold">
					{t("investor:investor.profile.choice.novice.title")}
				</strong>
				<br />
				{t("investor:investor.profile.choice.novice.copy.p1")}
				<br /> <br />
				{t("investor:investor.profile.choice.novice.copy.p2")}
			</p>

			<Button
				onClick={() => {
					openNoviceDialog()
				}}
				className="flex-end flex"
			>
				{t("investor:investor.profile.choice.novice.button")}
			</Button>
		</div>
	)
}

/**
 * StepCompletedOverlay
 * @returns
 */
const StepCompletedOverlay = () => {
	// Contants
	const VALIDITY_MONTHS = 24

	// i18n
	const t = useTrans("investor")
	const { formatDate } = useLang()

	// Context
	const { investorProfileExperience } = useInvestmentTests()

	// Template
	return (
		<div className="absolute inset-0 z-10 bg-gray-600 bg-opacity-50 p-5 text-center text-lg">
			<Card className="mx-auto max-w-md">
				<div className="space-y-4 text-center">
					<Heading
						as="h2"
						styleAs="h5"
						className="flex justify-center gap-2"
					>
						<div className="flex items-center">
							<CheckIcon
								aria-hidden="true"
								className="size-5 text-lg text-green-500"
							/>
						</div>
						Voltooid op{" "}
						{DateTime.fromISO(
							investorProfileExperience?.created_at,
						).toFormat(dateFormat)}
					</Heading>
					<p className="text-gray-500">
						Uw keuze was:{" "}
						{
							{
								NOVICE: "niet-ervaren belegger",
								EXPERT: "ervaren belegger",
							}[
								investorProfileExperience?.experience ||
									CoreInvestmentTestExperienceChoices.Novice
							]
						}
					</p>

					{/** Show remaining time until expiry date */}
					<p>
						{t("investor:investor.profile.generic.expiry-date", {
							validity: VALIDITY_MONTHS,
							date: formatDate(
								DateTime.fromISO(
									investorProfileExperience?.expiry_date,
								),
								{
									variant: "verbose",
								},
							),
						})}
					</p>
				</div>
			</Card>
		</div>
	)
}
